// src/pages/ProfilMenu.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Button,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Person as PersonIcon,
  Feedback as FeedbackIcon,
  Lock as LockIcon,
  ExitToApp as ExitToAppIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../api";

export default function ProfilMenu() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [profile, setProfile] = useState({
    nama: "",
    jabatan: "",
    photo: "https://randomuser.me/api/portraits/men/1.jpg",
    userId: "",
  });

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const parsed = JSON.parse(storedUser);
          const userId = parsed.nik || "";

          setProfile((prev) => ({
            ...prev,
            nama: parsed.nama || "",
            jabatan: parsed.jabatan || "",
            userId: userId,
          }));

          if (userId) {
            try {
              const res = await axios.get(`${API_URL}/profile-photo/${userId}`);
              if (res.data.success && res.data.base64Image) {
                localStorage.setItem("hasProfileImage", "true");
                setProfile((prev) => ({
                  ...prev,
                  photo: res.data.base64Image,
                }));
              } else {
                const hasProfileImage = localStorage.getItem("hasProfileImage");
                if (hasProfileImage === "true") {
                  localStorage.removeItem("hasProfileImage");
                }
              }
            } catch (err) {
              console.error("Error loading profile image:", err);
            }
          }
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Gagal memuat data profil");
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const uploadProfileImage = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("profileImage", file);
      formData.append("nik", profile.userId);

      const response = await axios.post(`${API_URL}/upload-profile`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        const res = await axios.get(
          `${API_URL}/profile-photo/${profile.userId}`
        );
        if (res.data.success && res.data.base64Image) {
          // Only store a reference in localStorage, not the full image
          localStorage.setItem("hasProfileImage", "true");
          setProfile((prev) => ({
            ...prev,
            photo: res.data.base64Image,
          }));
        }
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError("Gagal mengunggah foto profil");
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    uploadProfileImage(file);
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("hasProfileImage");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userName");
    localStorage.removeItem("userJabatan");
    localStorage.removeItem("userNik");
    localStorage.removeItem("userMinePermit");
    localStorage.removeItem("pathProfil");
    localStorage.removeItem("menuAccess");

    navigate("/");
  };

  const handleCloseError = () => {
    setError(null);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="form-container">
      <header className="app-header">
        <div className="header-content">
          <button className="back-button" onClick={() => navigate("/menu")}>
            <Icon>arrow_back</Icon>
          </button>
          <h1 className="header-title">Profil Karyawan</h1>
          <div className="header-spacer"></div>
        </div>
      </header>

      <Paper
        sx={{
          p: 3,
          textAlign: "center",
          backgroundColor: "#3498db",
          color: "white",
        }}
      >
        <Box
          sx={{
            position: "relative",
            margin: "0 auto",
            mb: 2,
            width: 120,
            height: 120,
          }}
        >
          <Avatar
            src={profile.photo}
            sx={{
              width: 120,
              height: 120,
              opacity: uploading ? 0.5 : 1,
            }}
          />
          {uploading && (
            <CircularProgress
              size={40}
              sx={{
                color: "white",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-20px",
                marginLeft: "-20px",
              }}
            />
          )}
        </Box>
        <Button
          variant="outlined"
          size="small"
          component="label"
          sx={{ borderColor: "white", color: "white" }}
          disabled={uploading}
        >
          {uploading ? "Mengunggah..." : "Ubah Foto"}
          <input
            type="file"
            accept="image/*"
            hidden
            onChange={handlePhotoChange}
            disabled={uploading}
          />
        </Button>
        <Typography variant="h6" sx={{ mt: 2 }}>
          {profile.nama}
        </Typography>
        <Typography variant="body2" color="white" sx={{ opacity: 0.8 }}>
          {profile.jabatan}
        </Typography>
      </Paper>

      {/* Menu Navigasi */}
      <Box mt={3} marginRight={4} marginLeft={4}>
        <Paper sx={{ p: 2 }}>
          <List>
            <ListItemButton onClick={() => navigate("/profil-karyawan")}>
              <ListItemIcon>
                <PersonIcon />
              </ListItemIcon>
              <ListItemText primary="Profil Karyawan" />
            </ListItemButton>

            {/* <ListItemButton onClick={() => navigate("/kritik-saran")}>
              <ListItemIcon>
                <FeedbackIcon />
              </ListItemIcon>
              <ListItemText primary="Kritik & Saran" />
            </ListItemButton> */}

            <ListItemButton onClick={() => navigate("/ganti-password")}>
              <ListItemIcon>
                <LockIcon />
              </ListItemIcon>
              <ListItemText primary="Ganti Kata Sandi" />
            </ListItemButton>

            <Divider />

            <ListItemButton onClick={() => setLogoutDialogOpen(true)}>
              <ListItemIcon>
                <ExitToAppIcon color="error" />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ color: "error" }}
              />
            </ListItemButton>
          </List>
        </Paper>
      </Box>

      {/* Dialog Konfirmasi Logout */}
      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
      >
        <DialogTitle>Konfirmasi Logout</DialogTitle>
        <DialogContent>Apakah Anda yakin ingin keluar dari akun?</DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>Batal</Button>
          <Button onClick={handleLogout} variant="contained" color="error">
            Keluar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </div>
  );
}
